import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Stack,
    FormControlLabel,
    Switch,
    Divider,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    AlertTitle,
    CircularProgress,
    Grid
} from '@mui/material';
import {
    Sparkles,
    Wand2,
    Lightbulb,
    RefreshCw,
    HelpCircle,
    MessageSquare,
    Clock,
    Calendar,
    Crown
} from 'lucide-react';

// Sample taglines for different roles and industries
const sampleTaglines = {
    technology: [
        "Building the digital future, one line of code at a time",
        "Transforming ideas into elegant technical solutions",
        "Creating technology that empowers people and businesses"
    ],
    design: [
        "Crafting experiences that inspire and delight",
        "Where creativity meets purpose",
        "Designing intuitive solutions to complex problems"
    ],
    marketing: [
        "Connecting brands with their audiences through compelling storytelling",
        "Creating strategies that drive growth and engagement",
        "Transforming insights into impactful marketing initiatives"
    ],
    finance: [
        "Driving financial excellence and sustainable growth",
        "Creating value through strategic financial management",
        "Building financial foundations for long-term success"
    ],
    healthcare: [
        "Committed to compassionate care and medical excellence",
        "Advancing healthcare through innovation and dedication",
        "Improving patient outcomes through personalized care"
    ],
    sales: [
        "Creating valuable partnerships that drive mutual success",
        "Delivering solutions that exceed expectations",
        "Connecting businesses with the tools they need to thrive"
    ],
    executive: [
        "Leading with vision, purpose, and strategic insight",
        "Driving organizational excellence and innovation",
        "Building teams and strategies that deliver exceptional results"
    ],
    legal: [
        "Providing strategic legal counsel with clarity and integrity",
        "Navigating complex legal challenges with expertise and precision",
        "Delivering legal solutions that protect and advance your interests"
    ]
};

// Sample follow-up prompts
const followUpPrompts = [
    "Looking forward to our meeting on {date}.",
    "I'll follow up with more details by {date}.",
    "Let's connect to discuss this further next week.",
    "Don't hesitate to reach out if you have any questions.",
    "I'd be happy to schedule a call to explore this opportunity further.",
    "Looking forward to your feedback on the proposal."
];

// Sample seasonal messages
const seasonalMessages = {
    "January": "Wishing you a successful start to the new year!",
    "February": "Looking forward to a collaborative month ahead.",
    "March": "As we move into spring, excited about new opportunities.",
    "April": "Hope your spring is off to a productive start!",
    "May": "Wishing you a wonderful and productive month of May.",
    "June": "As we approach mid-year, excited about what we've accomplished.",
    "July": "Enjoying the summer while staying productive!",
    "August": "Making the most of the final weeks of summer.",
    "September": "Energized for a productive fall season ahead.",
    "October": "Wishing you a fantastic fall season!",
    "November": "Grateful for our collaboration as the year winds down.",
    "December": "Wishing you happy holidays and a wonderful new year ahead!"
};

const AiSignatureContent = ({ 
    profileData, 
    isTrial = false,
    setSnackbar,
    onUpdateSignatureContent
}) => {
    const [tagline, setTagline] = useState("");
    const [followUpText, setFollowUpText] = useState("");
    const [seasonalMessage, setSeasonalMessage] = useState("");
    const [useAutoTagline, setUseAutoTagline] = useState(false);
    const [useFollowUp, setUseFollowUp] = useState(false);
    const [useSeasonal, setUseSeasonal] = useState(false);
    const [followUpDate, setFollowUpDate] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);

    useEffect(() => {
        // Set initial values when profile data changes or on first load
        if (profileData?.title) {
            const industry = getIndustryFromTitle(profileData.title);
            const month = new Date().toLocaleDateString('en-US', { month: 'long' });
            
            // Only set default values if not already set
            if (!tagline) {
                const defaultTagline = sampleTaglines[industry][0];
                setTagline(defaultTagline);
            }
            
            if (!seasonalMessage) {
                setSeasonalMessage(seasonalMessages[month]);
            }
            
            if (!followUpText) {
                const followUpDateObj = new Date();
                followUpDateObj.setDate(followUpDateObj.getDate() + 3);
                const formattedDate = followUpDateObj.toLocaleDateString('en-US', { 
                    month: 'long', 
                    day: 'numeric' 
                });
                setFollowUpDate(formattedDate);
                setFollowUpText(followUpPrompts[0].replace('{date}', formattedDate));
            }
        }
    }, [profileData]);
    
    // Effect to update parent component when toggles or content changes
    useEffect(() => {
        if (hasGeneratedOnce) {
            onUpdateSignatureContent(
                useAutoTagline ? tagline : "",
                useFollowUp ? followUpText : "",
                useSeasonal ? seasonalMessage : ""
            );
        }
    }, [useAutoTagline, useFollowUp, useSeasonal, tagline, followUpText, seasonalMessage, hasGeneratedOnce]);

    // Get industry from job title
    const getIndustryFromTitle = (title) => {
        const titleLower = title?.toLowerCase() || '';
        
        if (titleLower.includes('develop') || titleLower.includes('engineer') || 
            titleLower.includes('tech') || titleLower.includes('data')) {
            return 'technology';
        } else if (titleLower.includes('design') || titleLower.includes('creative') || 
                  titleLower.includes('art')) {
            return 'design';
        } else if (titleLower.includes('financ') || titleLower.includes('account') || 
                  titleLower.includes('invest')) {
            return 'finance';
        } else if (titleLower.includes('legal') || titleLower.includes('attorney') || 
                  titleLower.includes('lawyer')) {
            return 'legal';
        } else if (titleLower.includes('market') || titleLower.includes('brand') || 
                  titleLower.includes('content')) {
            return 'marketing';
        } else if (titleLower.includes('health') || titleLower.includes('medic') || 
                  titleLower.includes('doctor') || titleLower.includes('nurse')) {
            return 'healthcare';
        } else if (titleLower.includes('exec') || titleLower.includes('ceo') || 
                  titleLower.includes('director') || titleLower.includes('manager')) {
            return 'executive';
        } else if (titleLower.includes('sales') || titleLower.includes('business') ||
                  titleLower.includes('account exec')) {
            return 'sales';
        }
        
        return 'technology'; // Default industry
    };

    // Generate AI content
    const generateContent = () => {
        if (isTrial) {
            setSnackbar({
                open: true,
                message: 'AI content generation is a premium feature. Please upgrade to access.',
                severity: 'info'
            });
            return;
        }
        
        setIsGenerating(true);
        
        // This would be an actual API call in production
        setTimeout(() => {
            // Generate tagline based on industry
            const industry = getIndustryFromTitle(profileData.title);
            const industryTaglines = sampleTaglines[industry] || sampleTaglines.technology;
            const randomTagline = industryTaglines[Math.floor(Math.random() * industryTaglines.length)];
            setTagline(randomTagline);
            
            // Generate follow-up text
            // Create a date 3 business days from now
            const followUpDateObj = new Date();
            let daysToAdd = 3;
            followUpDateObj.setDate(followUpDateObj.getDate() + daysToAdd);
            // Format date as Month Day
            const formattedDate = followUpDateObj.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric' 
            });
            setFollowUpDate(formattedDate);
            
            const randomFollowUp = followUpPrompts[Math.floor(Math.random() * followUpPrompts.length)];
            setFollowUpText(randomFollowUp.replace('{date}', formattedDate));
            
            // Generate seasonal message
            const month = new Date().toLocaleDateString('en-US', { month: 'long' });
            setSeasonalMessage(seasonalMessages[month]);
            
            setIsGenerating(false);
            setHasGeneratedOnce(true);
            
            // Update signature content if toggles are on
            onUpdateSignatureContent(
                useAutoTagline ? randomTagline : tagline,
                useFollowUp ? randomFollowUp.replace('{date}', formattedDate) : followUpText,
                useSeasonal ? seasonalMessages[month] : seasonalMessage
            );
            
            setSnackbar({
                open: true,
                message: 'AI content generated successfully!',
                severity: 'success'
            });
        }, 1500);
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                AI Signature Content
            </Typography>
            
            {isTrial && (
                <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                    icon={<Crown size={20} />}
                >
                    <AlertTitle>Premium Feature</AlertTitle>
                    AI-powered dynamic signature content is available with a premium subscription.
                    <Button 
                        variant="outlined" 
                        size="small" 
                        sx={{ mt: 1 }}
                        color="primary"
                    >
                        Upgrade Now
                    </Button>
                </Alert>
            )}
            
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{ 
                        mb: 3, 
                        borderRadius: 2,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 6
                        }
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Wand2 size={20} />
                                    <Typography variant="h6">AI Content Generator</Typography>
                                </Box>
                                <Tooltip title="Generate personalized signature content using AI">
                                    <IconButton size="small">
                                        <HelpCircle size={16} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Let AI generate professional taglines, follow-up messages, and seasonal 
                                greetings based on your role, industry, and company.
                            </Typography>
                            
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <Sparkles />}
                                onClick={generateContent}
                                disabled={isGenerating || isTrial}
                                fullWidth
                                sx={{ mt: 1 }}
                            >
                                {isGenerating ? "Generating..." : "Generate AI Content"}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <Card sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                        }
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Lightbulb size={20} color="#F59E0B" />
                                <Typography variant="h6">Professional Tagline</Typography>
                            </Box>
                            
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={useAutoTagline}
                                        onChange={(e) => setUseAutoTagline(e.target.checked)}
                                        disabled={isTrial}
                                    />
                                }
                                label="Include in signature"
                            />
                            
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={tagline}
                                onChange={(e) => setTagline(e.target.value)}
                                placeholder="Your professional tagline"
                                variant="outlined"
                                sx={{ mt: 2 }}
                                disabled={isTrial}
                                InputProps={{
                                    readOnly: isTrial
                                }}
                            />
                            
                            <Button
                                variant="text"
                                startIcon={<RefreshCw size={16} />}
                                sx={{ mt: 1 }}
                                size="small"
                                onClick={() => {
                                    const industry = getIndustryFromTitle(profileData.title);
                                    const industryTaglines = sampleTaglines[industry] || sampleTaglines.technology;
                                    const randomTagline = industryTaglines[Math.floor(Math.random() * industryTaglines.length)];
                                    setTagline(randomTagline);
                                }}
                                disabled={isTrial}
                            >
                                Refresh
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <Card sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                        }
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <MessageSquare size={20} color="#2563EB" />
                                <Typography variant="h6">Follow-Up Note</Typography>
                            </Box>
                            
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={useFollowUp}
                                        onChange={(e) => setUseFollowUp(e.target.checked)}
                                        disabled={isTrial}
                                    />
                                }
                                label="Include in signature"
                            />
                            
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={followUpText}
                                onChange={(e) => setFollowUpText(e.target.value)}
                                placeholder="Your follow-up message"
                                variant="outlined"
                                sx={{ mt: 2 }}
                                disabled={isTrial}
                                InputProps={{
                                    readOnly: isTrial
                                }}
                            />
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Clock size={16} />
                                <Typography variant="caption" sx={{ ml: 1 }}>
                                    Follow-up date: {followUpDate}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <Card sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                        }
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Calendar size={20} color="#10B981" />
                                <Typography variant="h6">Seasonal Message</Typography>
                            </Box>
                            
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={useSeasonal}
                                        onChange={(e) => setUseSeasonal(e.target.checked)}
                                        disabled={isTrial}
                                    />
                                }
                                label="Include in signature"
                            />
                            
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={seasonalMessage}
                                onChange={(e) => setSeasonalMessage(e.target.value)}
                                placeholder="Your seasonal greeting"
                                variant="outlined"
                                sx={{ mt: 2 }}
                                disabled={isTrial}
                                InputProps={{
                                    readOnly: isTrial
                                }}
                            />
                            
                            <Chip
                                label={`Current Month: ${new Date().toLocaleDateString('en-US', { month: 'long' })}`}
                                size="small"
                                color="success"
                                sx={{ mt: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            
            <Divider sx={{ my: 4 }} />
            
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Preview</Typography>
                <Card sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                    <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2, color: '#4b5563' }}>
                        {useAutoTagline && tagline ? tagline : "No tagline selected"}
                    </Typography>
                    
                    {useFollowUp && followUpText && (
                        <Typography variant="body2" sx={{ mb: 1, color: '#6b7280' }}>
                            {followUpText}
                        </Typography>
                    )}
                    
                    {useSeasonal && seasonalMessage && (
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {seasonalMessage}
                        </Typography>
                    )}
                    
                    {!useAutoTagline && !useFollowUp && !useSeasonal && (
                        <Typography variant="body2" color="text.secondary">
                            Enable one or more AI content features to see them in your signature.
                        </Typography>
                    )}
                </Card>
            </Box>
        </Box>
    );
};

export default AiSignatureContent;